/**
 * Impersonation glue for master-admin "act-as-shop".
 *
 * Ships a global `window.fetch` interceptor that attaches
 *     X-Impersonated-Shop-Id: <shop uuid>
 * to every request bound for our own backend when the master admin
 * currently has an active act-as session (persisted in
 * sessionStorage.instacut_act_as_shop_id).
 *
 * The backend (auth_routes.get_caller) honours the header ONLY when the
 * calling JWT belongs to a master_admin — so a normal shop_admin or
 * barber cannot forge cross-shop access even if they set the header
 * themselves.
 *
 * Idempotent: multiple imports are safe. The wrap is applied once and
 * marked with a sentinel property.
 */

const STORAGE_KEY = 'instacut_act_as_shop_id';
const HEADER_NAME = 'X-Impersonated-Shop-Id';
const SENTINEL = '__instacut_fetch_wrapped';

export const getImpersonatedShopId = () => {
  try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
};

export const installImpersonationFetch = () => {
  if (typeof window === 'undefined' || !window.fetch) return;
  if (window.fetch[SENTINEL]) return;

  const backendOrigin = (() => {
    try { return new URL(process.env.REACT_APP_BACKEND_URL).origin; }
    catch { return null; }
  })();
  if (!backendOrigin) return;

  const originalFetch = window.fetch.bind(window);

  const wrapped = (input, init = {}) => {
    const shopId = getImpersonatedShopId();
    if (!shopId) return originalFetch(input, init);

    // Resolve the request URL. `input` can be a string, URL, or Request.
    let url;
    if (typeof input === 'string') url = input;
    else if (input instanceof URL) url = input.href;
    else if (input && input.url) url = input.url;
    else return originalFetch(input, init);

    let sameOrigin = false;
    try { sameOrigin = new URL(url, window.location.href).origin === backendOrigin; }
    catch { sameOrigin = false; }
    if (!sameOrigin) return originalFetch(input, init);

    // Merge the header without dropping anything the caller already set.
    const merged = { ...init };
    if (init.headers instanceof Headers) {
      const h = new Headers(init.headers);
      h.set(HEADER_NAME, shopId);
      merged.headers = h;
    } else if (Array.isArray(init.headers)) {
      merged.headers = [...init.headers, [HEADER_NAME, shopId]];
    } else {
      merged.headers = { ...(init.headers || {}), [HEADER_NAME]: shopId };
    }
    return originalFetch(input, merged);
  };

  wrapped[SENTINEL] = true;
  window.fetch = wrapped;
};
